import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/joy";
import {
  FileArrowUp,
  FilePdf,
  FileImage,
  DownloadSimple,
  Trash,
  ShieldCheck,
  BookOpen,
} from "@phosphor-icons/react";
import { apiClient, downloadApiBlob } from "../../lib/api";
import {
  dealError,
  proposalDealsApi,
  type Deal,
} from "../../lib/proposalDealsApi";

export function DealSupportingDocuments({
  deal,
  refresh,
}: {
  deal: Deal;
  refresh: () => Promise<void>;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  async function upload(files: FileList | null, category: string) {
    if (!files?.length) return;
    setPending(true);
    setError("");
    let revision = deal.revision;
    try {
      for (const file of Array.from(files)) {
        const data = new FormData();
        data.append("file", file);
        data.append("category", category);
        data.append("upload_id", crypto.randomUUID());
        data.append("revision", String(revision));
        const saved = await proposalDealsApi.action<Deal>(
          deal.id,
          "supporting-documents",
          data,
        );
        revision = saved.revision;
      }
    } catch (e) {
      setError(dealError(e));
    } finally {
      try {
        await refresh();
      } catch (e) {
        setError(dealError(e));
      } finally {
        setPending(false);
      }
    }
  }
  async function remove(id: number) {
    setPending(true);
    setError("");
    try {
      await apiClient.delete(
        `/api/proposal-deals/${deal.id}/supporting-documents/${id}/?revision=${deal.revision}`,
      );
      await refresh();
    } catch (e) {
      setError(dealError(e));
    } finally {
      setPending(false);
    }
  }
  return (
    <section aria-label="Proposal supporting documents">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={1}
        sx={{ mb: 2, mt: 1 }}
      >
        <Box>
          <Typography level="h4" sx={{ mb: 0.75 }}>
            Supporting documents
          </Typography>
          <Typography level="body-sm">
            Add the references your homeowner needs. Included in the web report
            and complete PDF.
          </Typography>
        </Box>
        <Chip
          size="sm"
          variant="soft"
          color="neutral"
          sx={{ alignSelf: "flex-start" }}
        >
          Optional
        </Chip>
      </Stack>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <div className="deal-grid deal-supporting-grid">
        {(
          [
            ["manufacturer", "Manufacturer specs & warranty"],
            ["building_codes", "Building codes"],
          ] as const
        ).map(([category, title]) => (
          <Card
            key={category}
            sx={{ p: { xs: 2, sm: 2.5 }, gap: 2, boxShadow: "none" }}
          >
            <Stack direction="row" gap={1.5} alignItems="center">
              <Box className="deal-feature-icon" sx={{ flexShrink: 0 }}>
                {category === "manufacturer" ? (
                  <ShieldCheck size={24} />
                ) : (
                  <BookOpen size={24} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="title-md">{title}</Typography>
                <Typography level="body-sm">
                  {category === "manufacturer"
                    ? "Product details, installation guides and coverage."
                    : "Applicable requirements for this project."}
                </Typography>
              </Box>
              <Chip size="sm" variant="soft">
                {
                  (deal.supporting_documents || []).filter(
                    (a) => a.category === category,
                  ).length
                }{" "}
                {(deal.supporting_documents || []).filter(
                  (a) => a.category === category,
                ).length === 1
                  ? "file"
                  : "files"}
              </Chip>
            </Stack>
            <Button
              component="label"
              variant="outlined"
              startDecorator={<FileArrowUp />}
              disabled={pending}
              sx={{
                py: 1.5,
                borderStyle: "dashed",
                bgcolor: "background.level1",
              }}
            >
              Upload{" "}
              {category === "manufacturer"
                ? "specs or warranty"
                : "building codes"}
              <input
                type="file"
                multiple
                accept="application/pdf,image/jpeg,image/png,image/webp"
                aria-label={`Upload ${title}`}
                hidden
                disabled={pending}
                onChange={(e) => {
                  void upload(e.target.files, category);
                  e.target.value = "";
                }}
              />
            </Button>
            <Typography level="body-xs">
              PDF, JPEG, PNG or WebP · Up to 20 MB per file
            </Typography>
            {(deal.supporting_documents || [])
              .filter((a) => a.category === category)
              .map((a) => (
                <Stack
                  key={a.id}
                  direction="row"
                  gap={1.25}
                  alignItems="center"
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    pt: 1.5,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ color: "primary.softColor", flexShrink: 0 }}>
                    {a.content_type === "application/pdf" ? (
                      <FilePdf size={26} />
                    ) : (
                      <FileImage size={26} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      level="title-sm"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {a.title}
                    </Typography>
                    <Typography level="body-xs">
                      {a.content_type === "application/pdf"
                        ? "PDF document"
                        : "Image"}{" "}
                      · Included in next report
                    </Typography>
                  </Box>
                  <IconButton
                    size="md"
                    variant="plain"
                    aria-label={`Download ${a.title}`}
                    onClick={() =>
                      void downloadApiBlob(
                        a.url,
                        a.title +
                          (a.content_type === "application/pdf"
                            ? ".pdf"
                            : ".jpg"),
                      ).catch((e) => setError(dealError(e)))
                    }
                  >
                    <DownloadSimple />
                  </IconButton>
                  <IconButton
                    size="md"
                    variant="plain"
                    color="danger"
                    disabled={pending}
                    aria-label={`Remove ${a.title} from future reports`}
                    onClick={() => void remove(a.id)}
                  >
                    <Trash />
                  </IconButton>
                </Stack>
              ))}
          </Card>
        ))}
      </div>
      <Typography level="body-xs" sx={{ mt: 1.5 }}>
        Updating these files affects future reports. Previously prepared reports
        keep their original attachments.
      </Typography>
    </section>
  );
}
